import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo, useRef, useState } from "react";

import { Impersonator } from "services/auth/types";

export interface User {
  id: string;
  email: string;
  name: string;
  [key: string]: any;
}

const SKEW_SECONDS = 30; // refresh proactively before expiry

const getMsUntilRefresh = (token: string): number | null => {
  try {
    const decoded = jwtDecode(token);

    if (!decoded.exp) {
      return null;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return (decoded.exp - SKEW_SECONDS - nowSeconds) * 1000;
  } catch {
    return null;
  }
};

const useWorkOS = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<User | null>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  const isValidatingRef = useRef(false);
  const lastActivityCheckRef = useRef<number>(0);

  const setAuthFromToken = useMemo(
    () => (token: string) => {
      try {
        const tokenPayload = jwtDecode(token) as User | null;

        if (tokenPayload && tokenPayload.exp * 1000 < Date.now()) {
          console.error("[useWorkOS] token has expired");
          setAccessToken(undefined);
          return false;
        }

        setAccessToken(token);
        setUser(tokenPayload);
        setError(null);
        return true;
      } catch (e) {
        console.error("[useWorkOS] token is invalid", e);
        setAccessToken(undefined);
        setUser(null);
        return false;
      }
    },
    [setAccessToken, setUser]
  );

  const startAuthFlow = async (email: string) => {
    try {
      setError(null);
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_DOORBELL_HOST}/v1/auth/start`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
          }),
        }
      );

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.error || "An error occurred, please try again.");
      }

      const contentType = res.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        const result = await res.json();
        if (result.authorisation_url) {
          return { authorisation_url: result.authorisation_url };
        }
      }

      return {};
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const verifyCode = async (email: string, code: string) => {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_DOORBELL_HOST}/v1/auth/verify`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          code,
        }),
      }
    );

    if (!res.ok) {
      const result = await res.json();
      throw new Error(result.error || "Unable to verify code");
    }

    const result = await res.json();
    const payload = jwtDecode(result.access_token) as User | null;

    if (!payload?.["urn:residently:user_id"]) {
      console.error("[useWorkOS] access token missing user_id claim");
      setAccessToken(undefined);
      setUser(null);
      throw new Error("missing_user_claim");
    }

    setAuthFromToken(result.access_token);
  };

  const checkSession = useMemo(
    () =>
      async (force = false) => {
        if (isValidatingRef.current) {
          return accessToken;
        }

        if (!force) {
          if (isLoading) {
            return "";
          }

          if (accessToken) {
            const ms = getMsUntilRefresh(accessToken);

            if (ms !== null && ms > 0) {
              return accessToken;
            }
          }
        }

        isValidatingRef.current = true;
        setError(null);
        try {
          const res = await fetch(
            `${process.env.NEXT_PUBLIC_DOORBELL_HOST}/v1/auth/refresh`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({}),
            }
          );

          if (!res.ok) {
            const result = await res.json();
            throw new Error(
              result.reason || result.error || "Unable to verify code"
            );
          }

          const result = await res.json();
          setAuthFromToken(result.access_token);
          setImpersonator(result.impersonator ?? null);

          return result.access_token;
        } catch (err) {
          console.warn(err);
          setError(err.message);
          setAccessToken(undefined);
          setUser(null);
          setImpersonator(null);
          setIsLoading(false);
          return "";
        } finally {
          isValidatingRef.current = false;
        }
      },
    [isLoading, accessToken]
  );

  const signOut = async () => {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_DOORBELL_HOST}/v1/auth/logout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.error || "Unable to logout");
      }

      setAccessToken(undefined);
      setUser(null);
      setImpersonator(null);
    } catch (err) {
      console.warn("Error during logout", err);
    }
  };

  useEffect(() => {
    if (accessToken) {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (enabled && isLoading) {
      checkSession(true);
    }
  }, [enabled, isLoading]);

  useEffect(() => {
    const THROTTLE_MS = 60_000;

    const handleActivity = () => {
      if (!accessToken) {
        return;
      }

      const now = Date.now();

      if (now - lastActivityCheckRef.current < THROTTLE_MS) {
        return;
      }

      lastActivityCheckRef.current = now;
      checkSession();
    };

    const events = ["mousemove", "click", "keydown", "touchstart"];
    events.forEach((event) => document.addEventListener(event, handleActivity));

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, handleActivity)
      );
    };
  }, [checkSession, accessToken]);

  return {
    error,
    user,
    isLoading: enabled ? isLoading : false,
    startAuthFlow,
    verifyCode,
    accessToken,
    checkSession,
    signOut,
    impersonator,
  };
};

export default useWorkOS;
